
import { JavaScriptCourse } from './types';

export const JS_INTERMEDIATE_COURSE: JavaScriptCourse = {
  id: 'js-intermediate',
  title: 'Intermediate JavaScript',
  description: 'Advance your JavaScript skills with modern ES6+ features, asynchronous programming, and advanced concepts.',
  level: 'intermediate',
  totalDuration: '10-12 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['JavaScript Fundamentals', 'Basic understanding of HTML/CSS', 'Familiarity with functions and objects'],
  learningOutcomes: [
    'Master advanced function concepts and closures',
    'Handle asynchronous operations with Promises and async/await',
    'Apply ES6+ features in real-world scenarios',
    'Implement object-oriented programming patterns',
    'Debug complex JavaScript applications',
    'Work with modern browser APIs'
  ],
  modules: [
    {
      id: 'js-advanced-functions',
      title: 'Advanced Functions',
      description: 'Deep dive into closures, higher-order functions, and functional programming concepts',
      duration: '90 minutes',
      difficulty: 'intermediate',
      objectives: [
        'Understand and use closures effectively',
        'Create and use higher-order functions',
        'Apply functional programming principles',
        'Master callback patterns'
      ],
      content: `# Advanced Functions

## Closures

Closures are one of JavaScript's most powerful features. A closure gives you access to an outer function's scope from an inner function.

\`\`\`javascript
function outerFunction(x) {
    // Outer function's variable
    let outerVariable = x;
    
    function innerFunction(y) {
        // Inner function has access to outerVariable
        return outerVariable + y;
    }
    
    return innerFunction;
}

let addFive = outerFunction(5);
console.log(addFive(3)); // 8 (5 + 3)

// Practical example: Counter with private state
function createCounter() {
    let count = 0;
    
    return {
        increment: () => ++count,
        decrement: () => --count,
        getCount: () => count
    };
}

let counter = createCounter();
console.log(counter.increment()); // 1
console.log(counter.increment()); // 2
console.log(counter.getCount());  // 2
// count is not directly accessible from outside
\`\`\`

## Higher-Order Functions

Functions that operate on other functions, either by taking them as arguments or by returning them.

\`\`\`javascript
// Function that takes another function as argument
function applyOperation(numbers, operation) {
    return numbers.map(operation);
}

let numbers = [1, 2, 3, 4, 5];
let squared = applyOperation(numbers, x => x * x);
console.log(squared); // [1, 4, 9, 16, 25]

// Function that returns another function
function createMultiplier(factor) {
    return function(number) {
        return number * factor;
    };
}

let double = createMultiplier(2);
let triple = createMultiplier(3);

console.log(double(5)); // 10
console.log(triple(5)); // 15
\`\`\`

## Function Methods: call, apply, bind

\`\`\`javascript
let person = {
    name: 'John',
    greet: function(greeting, punctuation) {
        return \`\${greeting}, \${this.name}\${punctuation}\`;
    }
};

let anotherPerson = { name: 'Jane' };

// call - invoke function with specific 'this' value
console.log(person.greet.call(anotherPerson, 'Hello', '!'));
// "Hello, Jane!"

// apply - same as call but arguments as array
console.log(person.greet.apply(anotherPerson, ['Hi', '.']));
// "Hi, Jane."

// bind - creates new function with bound 'this'
let boundGreet = person.greet.bind(anotherPerson);
console.log(boundGreet('Hey', '?')); // "Hey, Jane?"
\`\`\`

## Practical Examples

### Debouncing Function
\`\`\`javascript
function debounce(func, delay) {
    let timeoutId;
    
    return function(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
}

// Usage in search functionality
let searchInput = document.getElementById('search');
let debouncedSearch = debounce(function(event) {
    console.log('Searching for:', event.target.value);
    // Perform search operation
}, 300);

searchInput.addEventListener('input', debouncedSearch);
\`\`\`

### Function Composition
\`\`\`javascript
// Compose functions to create a pipeline
function compose(...functions) {
    return function(input) {
        return functions.reduce((result, fn) => fn(result), input);
    };
}

let addOne = x => x + 1;
let double = x => x * 2;
let square = x => x * x;

let pipeline = compose(addOne, double, square);
console.log(pipeline(3)); // ((3 + 1) * 2)^2 = 64
\`\`\``,
      practice: {
        task: 'Create a function factory that generates specialized functions',
        starter: `// Create a function factory for mathematical operations
// Requirements:
// 1. Create functions that remember their operation type
// 2. Support chaining operations
// 3. Implement a calculator with memory

// Add your code here`,
        solution: `// Mathematical function factory
function createCalculator() {
    let memory = 0;
    let history = [];
    
    return {
        // Basic operations
        add: function(value) {
            memory += value;
            history.push(\`+\${value}\`);
            return this; // Enable chaining
        },
        
        subtract: function(value) {
            memory -= value;
            history.push(\`-\${value}\`);
            return this;
        },
        
        multiply: function(value) {
            memory *= value;
            history.push(\`*\${value}\`);
            return this;
        },
        
        divide: function(value) {
            if (value !== 0) {
                memory /= value;
                history.push(\`/\${value}\`);
            } else {
                console.error('Division by zero!');
            }
            return this;
        },
        
        // Utility methods
        getValue: () => memory,
        
        getHistory: () => history.slice(),
        
        clear: function() {
            memory = 0;
            history = [];
            return this;
        },
        
        // Higher-order function for custom operations
        apply: function(operation) {
            let oldValue = memory;
            memory = operation(memory);
            history.push(\`custom(\${oldValue}) = \${memory}\`);
            return this;
        }
    };
}

// Usage examples
let calc = createCalculator();

// Chaining operations
let result = calc
    .add(10)
    .multiply(2)
    .subtract(5)
    .divide(3)
    .getValue();

console.log('Result:', result); // 5
console.log('History:', calc.getHistory());

// Using custom operations
calc.clear()
    .add(16)
    .apply(x => Math.sqrt(x))
    .apply(x => Math.pow(x, 3));

console.log('After custom operations:', calc.getValue()); // 64`,
        hints: [
          'Use closures to maintain private state',
          'Return "this" from methods to enable chaining',
          'Store operation history in an array',
          'Create higher-order functions that accept other functions'
        ]
      },
      quiz: {
        questions: [
          {
            id: 'js-advanced-functions-1',
            question: 'What is a closure in JavaScript?',
            options: [
              'A function that closes the browser',
              'A function that has access to variables in its outer scope',
              'A function that cannot be called',
              'A way to close loops'
            ],
            correctAnswer: 1,
            difficulty: 'medium',
            explanation: 'A closure is a function that has access to variables in its outer (enclosing) scope even after the outer function has returned.',
            points: 15
          }
        ],
        passThreshold: 75
      }
    },
    {
      id: 'js-async-programming',
      title: 'Asynchronous JavaScript',
      description: 'Master Promises, async/await, and handling asynchronous operations',
      duration: '120 minutes',
      difficulty: 'intermediate',
      objectives: [
        'Understand the event loop and asynchronous nature of JavaScript',
        'Work with Promises and Promise chains',
        'Use async/await for cleaner asynchronous code',
        'Handle errors in asynchronous operations',
        'Fetch data from APIs'
      ],
      content: `# Asynchronous JavaScript

## Understanding Asynchronous Programming

JavaScript is single-threaded but can handle asynchronous operations through the event loop.

\`\`\`javascript
console.log('1 - Start');

setTimeout(() => {
    console.log('2 - Timeout callback');
}, 0);

console.log('3 - End');

// Output: 1 - Start, 3 - End, 2 - Timeout callback
\`\`\`

## Promises

Promises represent the eventual completion or failure of an asynchronous operation.

\`\`\`javascript
// Creating a Promise
let myPromise = new Promise((resolve, reject) => {
    let success = Math.random() > 0.5;
    
    setTimeout(() => {
        if (success) {
            resolve('Operation successful!');
        } else {
            reject('Operation failed!');
        }
    }, 1000);
});

// Using the Promise
myPromise
    .then(result => {
        console.log('Success:', result);
    })
    .catch(error => {
        console.log('Error:', error);
    });
\`\`\`

## Promise Chaining

\`\`\`javascript
function fetchUser(id) {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve({ id: id, name: \`User \${id}\` });
        }, 500);
    });
}

function fetchUserPosts(userId) {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve([\`Post 1 by \${userId}\`, \`Post 2 by \${userId}\`]);
        }, 500);
    });
}

// Chaining promises
fetchUser(1)
    .then(user => {
        console.log('User:', user);
        return fetchUserPosts(user.id);
    })
    .then(posts => {
        console.log('Posts:', posts);
    })
    .catch(error => {
        console.log('Error occurred:', error);
    });
\`\`\`

## Async/Await

A more readable way to work with asynchronous code.

\`\`\`javascript
async function fetchUserData(id) {
    try {
        console.log('Fetching user...');
        let user = await fetchUser(id);
        console.log('User:', user);
        
        console.log('Fetching posts...');
        let posts = await fetchUserPosts(user.id);
        console.log('Posts:', posts);
        
        return { user, posts };
    } catch (error) {
        console.log('Error occurred:', error);
        throw error; // Re-throw if needed
    }
}

// Using async function
fetchUserData(1)
    .then(data => console.log('Complete data:', data))
    .catch(error => console.log('Failed to fetch data:', error));
\`\`\`

## Fetch API

Modern way to make HTTP requests.

\`\`\`javascript
async function fetchData(url) {
    try {
        let response = await fetch(url);
        
        if (!response.ok) {
            throw new Error(\`HTTP error! status: \${response.status}\`);
        }
        
        let data = await response.json();
        return data;
    } catch (error) {
        console.error('Fetch error:', error);
        throw error;
    }
}

// Using fetch
async function displayUsers() {
    try {
        let users = await fetchData('https://jsonplaceholder.typicode.com/users');
        console.log('Users:', users);
        
        // Display users in DOM
        let userList = document.getElementById('user-list');
        userList.innerHTML = users
            .map(user => \`<li>\${user.name} - \${user.email}</li>\`)
            .join('');
            
    } catch (error) {
        console.error('Failed to load users:', error);
    }
}
\`\`\`

## Promise Utilities

\`\`\`javascript
// Promise.all - wait for all promises to resolve
let promise1 = fetch('/api/data1');
let promise2 = fetch('/api/data2');
let promise3 = fetch('/api/data3');

Promise.all([promise1, promise2, promise3])
    .then(responses => {
        console.log('All requests completed');
        return Promise.all(responses.map(r => r.json()));
    })
    .then(data => {
        console.log('All data:', data);
    });

// Promise.race - returns first resolved promise
Promise.race([promise1, promise2, promise3])
    .then(firstResponse => {
        console.log('First response:', firstResponse);
    });

// Promise.allSettled - wait for all promises to settle
Promise.allSettled([promise1, promise2, promise3])
    .then(results => {
        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                console.log(\`Promise \${index} succeeded:, result.value\`);
            } else {
                console.log(\`Promise \${index} failed:, result.reason\`);
            }
        });
    });
\`\`\``,
      practice: {
        task: 'Create a weather app that fetches data from multiple APIs',
        starter: `// Weather App
// Create functions that:
// 1. Fetch current weather
// 2. Fetch 5-day forecast
// 3. Handle loading states
// 4. Display data in DOM

// Mock API functions (replace with real API calls)
function fetchCurrentWeather(city) {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve({
                city: city,
                temperature: Math.round(Math.random() * 30 + 10),
                condition: 'Sunny',
                humidity: Math.round(Math.random() * 50 + 30)
            });
        }, Math.random() * 1000 + 500);
    });
}

// Add your code here`,
        hints: [
          'Use async/await for cleaner asynchronous code',
          'Handle errors with try/catch blocks',
          'Show loading indicators while fetching data',
          'Use Promise.all to fetch multiple data sources simultaneously',
          'Update the DOM with fetched data'
        ]
      }
    }
  ]
};
